import { useEffect } from 'react';

const CHATWOOT_BASE_URL = 'https://app.chatwoot.com';

/**
 * Custom hook to initialize Chatwoot chat widget
 * Requires REACT_APP_CHATWOOT_WEBSITE_TOKEN environment variable
 * @param {Object} user - User object containing email and other user information
 */
const useChatwoot = (user) => {
  useEffect(() => {
    const websiteToken = process.env.REACT_APP_CHATWOOT_WEBSITE_TOKEN;
    
    // Don't initialize if no token is provided
    if (!websiteToken) {
      console.warn('Chatwoot: No REACT_APP_CHATWOOT_WEBSITE_TOKEN found in environment variables');
      return;
    }

    // Check if Chatwoot is already loaded
    if (window.$chatwoot) {
      return;
    }

    // Add Chatwoot Settings
    window.chatwootSettings = {
      hideMessageBubble: true, // Hide default bubble, we'll use custom button
      position: 'right', // can be 'left' or 'right'
      locale: 'en', // can be changed to user's language
      type: 'standard', // can be 'standard' or 'expanded_bubble'
    };

    // Load Chatwoot SDK
    (function (d, t) {
      var BASE_URL = CHATWOOT_BASE_URL;
      var g = d.createElement(t);
      var s = d.getElementsByTagName(t)[0];
      g.src = BASE_URL + '/packs/js/sdk.js';
      g.defer = true;
      g.async = true;
      s.parentNode.insertBefore(g, s);
      
      g.onload = function () {
        window.chatwootSDK.run({
          websiteToken: websiteToken,
          baseUrl: BASE_URL,
        });
      };
    })(document, 'script');

    // Cleanup function
    return () => {
      // Chatwoot doesn't provide a cleanup method, so we just remove the reference
      if (window.$chatwoot) {
        delete window.$chatwoot;
      }
    };
  }, []);

  // Set user information when user data is available and Chatwoot is loaded
  useEffect(() => {
    const setUserInfo = () => {
      if (user && window.$chatwoot) {
        // Set user information in Chatwoot
        window.$chatwoot.setUser(user.id, {
          email: user.email,
          name: user.name || user.username || user.email,
          // Add any other relevant user information
          ...(user.phone_number && { phone_number: user.phone_number }),
        });
      }
    };

    // If Chatwoot is already loaded, set user info immediately
    if (window.$chatwoot) {
      setUserInfo();
    } else if (user) {
      // If Chatwoot is not loaded yet, wait for it with a listener
      const checkChatwoot = setInterval(() => {
        if (window.$chatwoot) {
          setUserInfo();
          clearInterval(checkChatwoot);
        }
      }, 100); // Check every 100ms

      // Cleanup interval on unmount or user change
      return () => clearInterval(checkChatwoot);
    }
  }, [user]);
};

export default useChatwoot;
